import React from 'react';

import { PricingPlanComparisonItem } from '@repo/editor/src/types/Pricing';
import { withEventScope } from '@repo/event-processor';
import { Button, primarySmallTheme } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';
import { H3 } from '@repo/ui/components/Typography';

import { getComparisonKeys } from '../comparisonData';

import Item from './Item';
import RowTitle from './RowTitle';
import { TableBody, Row, SectionTitle } from './styles';
import { ComparisonTableProps } from './types';

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  plans,
  selectedExpertAddon,
}) => {
  const renderLabel = (
    comparison: PricingPlanComparisonItem | { contactSales: boolean }
  ): string | undefined => {
    return 'label' in comparison
      ? typeof comparison.label === 'string'
        ? comparison.label
        : comparison.label(selectedExpertAddon?.generationCredits)
      : undefined;
  };

  const COMPARISON_KEYS = getComparisonKeys();

  return (
    <table>
      <TableBody data-testid="pricing-page--plans-comparison-table">
        <Row columns={plans.length + 1}>
          <td />
          {plans.map((plan) => (
            <td key={plan.title}>
              <H3 style={{ textAlign: 'center' }}>{plan.title}</H3>
              <Spacer height="16px" />
              <Button
                name="comparisonTablePlanButton"
                {...plan.button}
                disabled={plan.button?.disabled}
                height="30px"
                label={plan.button?.label ?? plan.buttonLabel}
                theme={plan.button.theme ?? primarySmallTheme}
                width="100%"
              />
            </td>
          ))}
        </Row>
        <Row columns={plans.length + 1}>
          <SectionTitle>Features</SectionTitle>
          {plans.map((plan) => (
            <SectionTitle key={plan.title} />
          ))}
        </Row>
        {COMPARISON_KEYS.features.map(({ label, tooltip }, index) => (
          <Row key={label} columns={plans.length + 1}>
            <RowTitle label={label} tooltip={tooltip} />
            {plans.map((plan) => {
              const comparison = plan.comparison.features[index];
              return (
                <Item
                  key={plan.title}
                  check={'check' in comparison ? comparison.check : undefined}
                  label={renderLabel(comparison)}
                />
              );
            })}
          </Row>
        ))}
        <Row columns={plans.length + 1}>
          <SectionTitle>AI Design Tools</SectionTitle>
          {plans.map((plan) => (
            <SectionTitle key={plan.title} />
          ))}
        </Row>
        {COMPARISON_KEYS.aiDesignTools.map(
          ({ label, subtitle, tooltip }, index) => (
            <Row key={label} columns={plans.length + 1}>
              <RowTitle label={label} subtitle={subtitle} tooltip={tooltip} />
              {plans.map((plan) => {
                const comparison = plan.comparison.aiDesignTools[index];
                return (
                  <Item
                    key={plan.title}
                    check={'check' in comparison ? comparison.check : undefined}
                    infoIconModalContent={
                      'infoIconModalContent' in comparison
                        ? comparison.infoIconModalContent
                        : undefined
                    }
                    label={renderLabel(comparison)}
                  />
                );
              })}
            </Row>
          )
        )}
        <Row columns={plans.length + 1}>
          <SectionTitle>Licensing</SectionTitle>
          {plans.map((plan) => (
            <SectionTitle key={plan.title} />
          ))}
        </Row>
        {COMPARISON_KEYS.licensing.map(({ label, tooltip }, index) => (
          <Row key={label} columns={plans.length + 1}>
            <RowTitle label={label} tooltip={tooltip} />
            {plans.map((plan) => {
              const comparison = plan.comparison.licensing[index];
              return (
                <Item
                  key={plan.title}
                  check={'check' in comparison ? comparison.check : undefined}
                  label={renderLabel(comparison)}
                />
              );
            })}
          </Row>
        ))}
        <Row columns={plans.length + 1}>
          <SectionTitle>Content</SectionTitle>
          {plans.map((plan) => (
            <SectionTitle key={plan.title} />
          ))}
        </Row>
        {COMPARISON_KEYS.content.map(({ label, tooltip }, index) => (
          <Row key={label} columns={plans.length + 1}>
            <RowTitle label={label} tooltip={tooltip} />
            {plans.map((plan) => {
              const comparison = plan.comparison.content[index];
              return (
                <Item
                  key={plan.title}
                  check={'check' in comparison ? comparison.check : undefined}
                  label={renderLabel(comparison)}
                />
              );
            })}
          </Row>
        ))}
        <Row columns={plans.length + 1}>
          <SectionTitle>Business Add-Ons</SectionTitle>
          {plans.map((plan) => (
            <SectionTitle key={plan.title} />
          ))}
        </Row>
        {COMPARISON_KEYS.businessAddons.map(({ label, tooltip }, index) => (
          <Row key={label} columns={plans.length + 1}>
            <RowTitle label={label} tooltip={tooltip} />
            {plans.map((plan) => {
              const comparison = plan.comparison.businessAddons[index];
              return (
                <Item
                  key={plan.title}
                  check={'check' in comparison ? comparison.check : undefined}
                  contactSales={
                    'contactSales' in comparison
                      ? comparison.contactSales
                      : undefined
                  }
                  label={renderLabel(comparison)}
                />
              );
            })}
          </Row>
        ))}
      </TableBody>
    </table>
  );
};

export default withEventScope(ComparisonTable, {
  module: 'pricingPlansComparisonTable',
});
